import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Paper,
  IconButton,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Close,
  Dashboard,
  People,
  Settings,
  Security,
  NetworkCheck,
  Assignment,
  AccountBalance,
  QrCodeScanner,
  Analytics,
  CheckCircle,
  ArrowForward,
  ArrowBack
} from '@mui/icons-material';

const AdminTour = ({ isOpen, onClose, onComplete }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [completed, setCompleted] = useState({});

  const steps = [
    {
      label: 'Welcome to HarvestAI Admin',
      icon: <Dashboard />,
      content: {
        title: 'Welcome to Your Admin Dashboard!',
        description: 'HarvestAI is a comprehensive facility management system with AI-powered features. This tour will guide you through the main admin features.',
        features: [
          'Smart facility monitoring and control',
          'AI-powered employee access management',
          'Advanced task and budget management',
          'UniFi network integration',
          'Real-time analytics and reporting'
        ]
      }
    },
    {
      label: 'Dashboard Overview',
      icon: <Analytics />,
      content: {
        title: 'Dashboard & Analytics',
        description: 'Your central command center for monitoring all facility operations.',
        features: [
          'Real-time sensor data and room status',
          'Employee access logs and activity',
          'System health and performance metrics',
          'Quick action buttons for common tasks',
          'Customizable widgets and layouts'
        ]
      }
    },
    {
      label: 'Facility Management',
      icon: <People />,
      content: {
        title: 'Employee & Access Management',
        description: 'Manage your workforce and control facility access with AI-powered security.',
        features: [
          'Employee registration and profiles',
          'QR code and phone-based access',
          'AI face recognition for secure entry',
          'Visitor management system',
          'Access logs and audit trails'
        ]
      }
    },
    {
      label: 'Task Management',
      icon: <Assignment />,
      content: {
        title: 'Project & Task Management',
        description: 'Organize projects, track tasks, and manage team workload efficiently.',
        features: [
          'Create and assign tasks to team members',
          'Track project progress and deadlines',
          'Budget estimation and tracking',
          'Team workload distribution',
          'Project reporting and analytics'
        ]
      }
    },
    {
      label: 'Budget & Finance',
      icon: <AccountBalance />,
      content: {
        title: 'Budget Management',
        description: 'Track expenses, manage budgets, and generate financial reports.',
        features: [
          'Project budget creation and tracking',
          'Expense categorization and monitoring',
          'Financial forecasting and planning',
          'Cost analysis and optimization',
          'Budget vs actual reporting'
        ]
      }
    },
    {
      label: 'Network Management',
      icon: <NetworkCheck />,
      content: {
        title: 'UniFi Network Integration',
        description: 'Manage your UniFi network infrastructure directly from the admin panel.',
        features: [
          'Network device monitoring and control',
          'Camera management and surveillance',
          'Access point configuration',
          'Network diagnostics and troubleshooting',
          'Security policy management'
        ]
      }
    },
    {
      label: 'AI Features',
      icon: <Security />,
      content: {
        title: 'AI-Powered Features',
        description: 'Leverage artificial intelligence for enhanced security and automation.',
        features: [
          'AI face recognition for access control',
          'Smart camera monitoring',
          'Automated access validation',
          'Intelligent threat detection',
          'Predictive analytics'
        ]
      }
    },
    {
      label: 'System Settings',
      icon: <Settings />,
      content: {
        title: 'System Configuration',
        description: 'Customize your system settings and branding.',
        features: [
          'Company branding and customization',
          'User roles and permissions',
          'System preferences and defaults',
          'Integration settings',
          'Backup and maintenance'
        ]
      }
    }
  ];

  const handleNext = () => {
    setCompleted(prev => ({ ...prev, [activeStep]: true }));
    if (activeStep === steps.length - 1) {
      handleComplete();
    } else {
      setActiveStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleComplete = () => {
    localStorage.setItem('adminTourCompleted', 'true');
    onComplete && onComplete();
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem('adminTourCompleted', 'true');
    onClose();
  };

  const isStepCompleted = (step) => {
    return completed[step];
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
          borderRadius: 3,
          border: '2px solid rgba(102, 126, 234, 0.3)',
          minHeight: '70vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: '12px 12px 0 0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Admin Panel Tour
          </Typography>
          <Chip 
            label={`${activeStep + 1} of ${steps.length}`} 
            size="small" 
            sx={{ background: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
          />
        </Box>
        <IconButton onClick={handleSkip} sx={{ color: 'white' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Stepper */}
          <Box sx={{ width: 300, flexShrink: 0 }}>
            <Stepper activeStep={activeStep} orientation="vertical">
              {steps.map((step, index) => (
                <Step key={step.label} completed={isStepCompleted(index)}>
                  <StepLabel
                    StepIconComponent={() => (
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        background: activeStep === index 
                          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                          : isStepCompleted(index)
                          ? '#4caf50'
                          : 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        border: activeStep === index ? '2px solid #667eea' : 'none'
                      }}>
                        {isStepCompleted(index) ? <CheckCircle sx={{ fontSize: 20 }} /> : step.icon}
                      </Box>
                    )}
                    sx={{
                      '& .MuiStepLabel-label': {
                        color: activeStep === index ? '#667eea' : 'rgba(255, 255, 255, 0.7)',
                        fontWeight: activeStep === index ? 600 : 400,
                      }
                    }}
                  >
                    {step.label}
                  </StepLabel>
                  <StepContent>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      {step.content.description.substring(0, 60)}...
                    </Typography>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1 }}>
            <Paper sx={{ 
              p: 3, 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: 2
            }}>
              <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
                {steps[activeStep].content.title}
              </Typography>
              
              <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 3, lineHeight: 1.6 }}>
                {steps[activeStep].content.description}
              </Typography>

              <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

              <Typography variant="h6" sx={{ color: 'white', mb: 2 }}>
                Key Features:
              </Typography>

              <List sx={{ p: 0 }}>
                {steps[activeStep].content.features.map((feature, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ color: '#4caf50', fontSize: 20 }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary={feature}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.95rem'
                        }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <Button
            onClick={handleSkip}
            sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
          >
            Skip Tour
          </Button>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              startIcon={<ArrowBack />}
              sx={{
                color: 'rgba(255, 255, 255, 0.8)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Previous
            </Button>
            
            <Button
              onClick={handleNext}
              endIcon={activeStep === steps.length - 1 ? <CheckCircle /> : <ArrowForward />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                }
              }}
            >
              {activeStep === steps.length - 1 ? 'Complete Tour' : 'Next'}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AdminTour; 